import type { gameInfoType, LanguageType } from '@/pages/opinion/types/common';
import { useMemo } from 'react';
import { feedsColumnsFunc } from '../columns/feeds';
import { useIntl } from 'umi';
import type { SortBy } from 'tea-component/lib/table/addons';
import { getColumnsAndSortColumns } from '../utils';
import { useMetricsChannel } from '@op/hooks/useMetricsChannel';
import { useGetTopicFilter } from '@op/hooks/useTranslateTopic';

export function useSocialMediaColumns({
  isSpecialChannel,
  gameInfo,
  wholeGameInfo,
  languages,
  renderKey = '',
  sort,
  setSort,
  isExcludeInvalid,
  setIsExcludeInvalid,
  showSentiment = false,
  total = undefined,
  logCommentMap,
  logLoading,
  setLanguages,
  detailParams,
  channelList,
}: {
  isSpecialChannel?: boolean;
  gameInfo: gameInfoType;
  wholeGameInfo: any;
  languages: Array<LanguageType>;
  renderKey?: string | { origin: string[]; zh: string[]; en: string[] };
  sort: SortBy<any>;
  setSort: (sort: SortBy<any>) => void;
  isExcludeInvalid?: boolean;
  setIsExcludeInvalid?: (value: boolean) => void;
  showSentiment?: boolean;
  total?: number;
  logCommentMap?: { [key: string]: string };
  logLoading?: boolean;
  setLanguages?: any;
  detailParams?: any;
  channelList?: Array<string>;
}) {
  const { formatMessage } = useIntl();
  const MergeChannelIconElement = useMetricsChannel({ gameInfo });
  const { topicList } = useGetTopicFilter({ gameInfo });
  const getKeys = (): [Array<string>, string] => {
    const languageSort = ['origin', 'en', 'zh'];
    const languageList = languageSort.filter((a: any) =>
      languages?.includes(a),
    );
    const { edition_unified_id } = gameInfo;
    let keys: Array<string> = [
      'channelName',
      ...languageList,
      'validation',
      'monitoring',
      'reviewsType',
      'commentTime',
      'language',
      'topic',
      'engagement',
      'like',
      'dislike',
      'share',
      'comment',
      'view',
      'follower',
      'images',
      'liveViews',
      'description',
    ];

    if (!channelList?.includes('chzzk')) {
      keys = keys.filter(item => item !== 'liveViews');
    }

    if (isSpecialChannel) {
      if (edition_unified_id === 'ebaf4c80cdf9f22c672b348e8b19d57c8') {
        keys = [
          'channelName',
          ...languageList,
          'validation',
          'monitoring',
          'commentTime2',
          'updateTime',
          'view',
          'tweetsReply',
          'language',
          'topic',
        ];
      }
      if (
        [
          'u10000000066',
          'u10000000022',
          'u6d721eb69ef7de198bb84be26be6d40b',
        ].includes(edition_unified_id)
      ) {
        keys = [
          'channelName',
          ...languageList,
          'validation',
          'monitoring',
          'commentTime',
          'language',
          'topic',
        ];
      }

      if (edition_unified_id === 'u10000000028') {
        keys = [
          'channelName',
          ...languageList,
          'validation',
          'monitoring',
          'commentTime',
          'language',
          'topic',
          'ctgr1',
          'ctgr2',
          'ctgr3',
        ];
      }

      if (edition_unified_id === 'e11000000297') {
        keys = [
          'channelName',
          ...languageList,
          'validation',
          'reactions',
          'like2',
          'comment',
          'commentTime',
          'language',
          'topic',
        ];
      }
    }

    return [keys, languageList[0]];
  };

  const { columns, sortColumns, selectSortKeys } = useMemo(() => {
    const [keys, firstLanguage] = getKeys();
    const { entity_type } = gameInfo;
    const columnsMap = feedsColumnsFunc({
      formatMessage,
      entityType: entity_type,
      firstLanguage,
      renderKey,
      topicList,
      sort,
      setSort,
      excludeInvalidInfo: [isExcludeInvalid, setIsExcludeInvalid],
      showSentiment,
      total,
      logCommentMap,
      logLoading,
      MergeChannelIconElement,
      languages,
      setLanguages,
      detailParams,
      isSocialMedia: true,
      wholeGameInfo,
    });
    return getColumnsAndSortColumns(keys, columnsMap);
  }, [
    formatMessage,
    languages,
    renderKey,
    topicList,
    sort,
    isExcludeInvalid,
    showSentiment,
    total,
    logCommentMap,
    logLoading,
    channelList,
  ]);

  return { columns, sortColumns, selectSortKeys };
}
